import { FormError, Label, Textarea } from "@simplicity/ui";
import { useEffect, useRef, useState } from "react";
import { tryParseJson } from "../../../utils";
import { TablesEditor, type Schedules, type TableConfig } from "../tables";

export interface ConnectorFormProps {
  vars: Record<string, unknown>;
  onVarsChange: (vars: Record<string, unknown>) => void;
  connectorType: string;
  connectorPrefix: string;
  /** SIM-3182: present once the connector row exists; enables "Add from…". */
  connectorId?: string;
  destinationType?: string;
  clearBanner: () => void;
  /** When true, typed forms skip their Advanced Options section. */
  hideAdvanced?: boolean;
}

/** Extract tables from vars with safe coercion. */
function extractTables(vars: Record<string, unknown>): TableConfig[] {
  return Array.isArray(vars.tables) ? (vars.tables as TableConfig[]) : [];
}

/** Extract schedules from vars with safe coercion. */
function extractSchedules(vars: Record<string, unknown>): Schedules {
  const s = vars.schedules;
  return (s != null && typeof s === "object" && !Array.isArray(s) ? s : {}) as Schedules;
}

/**
 * Strip reserved keys to get "other vars". `scheduleDefs` is reserved because the
 * Schedules panel owns it — leaving it here would give one field two editors.
 */
function getOtherVars(vars: Record<string, unknown>): Record<string, unknown> {
  const { tables: _t, schedules: _s, scheduleDefs: _d, baseUrl: _b, ...rest } = vars;
  return rest;
}

export function GenericVarsForm(props: ConnectorFormProps) {
  const tables = extractTables(props.vars);
  const schedules = extractSchedules(props.vars);
  const otherVarsObj = getOtherVars(props.vars);

  const [otherText, setOtherText] = useState(JSON.stringify(otherVarsObj, null, 2));
  const [otherError, setOtherError] = useState<string | null>(null);

  // Sync local text when vars change externally (e.g., tables editor updates)
  const lastOtherRef = useRef(otherVarsObj);
  useEffect(() => {
    const currentJson = JSON.stringify(otherVarsObj, null, 2);
    const lastJson = JSON.stringify(lastOtherRef.current, null, 2);
    if (currentJson !== lastJson) {
      lastOtherRef.current = otherVarsObj;
      setOtherText(currentJson);
    }
  }, [otherVarsObj]);

  function handleOtherVarsChange(text: string) {
    setOtherText(text);
    setOtherError(null);
    const parsed = tryParseJson(text);
    if (parsed.ok) {
      const parsedObj = parsed.value as Record<string, unknown>;
      // Keep tables/schedules/baseUrl from props.vars, override rest
      const merged: Record<string, unknown> = { ...props.vars, ...parsedObj };
      merged.tables = props.vars.tables;
      merged.schedules = props.vars.schedules;
      merged.scheduleDefs = props.vars.scheduleDefs;
      if (props.vars.baseUrl !== undefined) merged.baseUrl = props.vars.baseUrl;
      props.onVarsChange(merged);
    }
    props.clearBanner();
  }

  return (
    <>
      <TablesEditor
        tables={tables}
        schedules={schedules}
        onChange={(change) => {
          props.onVarsChange({ ...props.vars, ...change });
          props.clearBanner();
        }}
        connectorType={props.connectorType}
        connectorId={props.connectorId}
      />
      <div>
        <Label htmlFor="conn-other-vars" className="form-label">
          Other Variables
        </Label>
        <Textarea
          id="conn-other-vars"
          className={`min-h-[100px] font-mono ${otherError ? "border-destructive" : ""}`}
          value={otherText}
          onChange={(e) => handleOtherVarsChange(e.target.value)}
          onBlur={() => {
            const r = tryParseJson(otherText);
            setOtherError(r.ok ? null : r.error);
          }}
        />
        {otherError && <FormError>{otherError}</FormError>}
      </div>
    </>
  );
}
